import { Decimal, dec } from '@app/shared';
import type { DocumentDto } from '@/entities/document';
import type { AllocationRow } from '@/features/payment/allocation-grid';

export type CashOrderType = 'CASH_IN' | 'CASH_OUT';

/** ПКО — деньги в кассу (получили), РКО — деньги из кассы (отдали). Направление жёстко задано
 * типом окна — в отличие от `PaymentWindow`, здесь нет Segmented-переключателя. */
export function directionOf(type: CashOrderType): 'IN' | 'OUT' {
  return type === 'CASH_IN' ? 'IN' : 'OUT';
}

export function prefixOf(type: CashOrderType): string {
  return type === 'CASH_IN' ? 'ПКО' : 'РКО';
}

export interface CashOrderHeader {
  docDate: string;
  counterpartyId?: string;
  amount: string | null;
  method: 'CASH' | 'CASHLESS' | 'TRANSFER';
  cashOperationType: 'PAYMENT' | 'REFUND';
  comment: string;
}

/** §7: обязательные поля при проведении — зеркало `assertReadyToPost` на бэкенде. */
export function isCashOrderComplete(header: CashOrderHeader): boolean {
  return !!header.counterpartyId && !!header.docDate && !!header.amount && dec(header.amount).gt(0) && !!header.method && !!header.cashOperationType;
}

/** Почему ордер нельзя провести — для подсказки на недоступной кнопке и сообщения при нажатии
 *  клавиши; `null`, если можно. Вердикт («полон ли ордер») за `isCashOrderComplete`: `null` ⇔ полон. */
export function getCashOrderBlockReason(header: CashOrderHeader): string | null {
  if (!header.docDate) return 'Укажите дату';
  if (!header.counterpartyId) return 'Укажите контрагента';
  if (!header.amount) return 'Укажите сумму';
  if (!dec(header.amount).gt(0)) return 'Сумма должна быть больше нуля';
  return isCashOrderComplete(header) ? null : 'Заполните обязательные поля';
}

const STATUS_SUFFIX: Record<string, string> = { DRAFT: ' (черновик)', CANCELLED: ' (отменён)' };

/** «Новый приходный ордер» / «ПКО-5/2026» / «ПКО-5/2026 (черновик)». */
export function windowTitle(type: CashOrderType, doc: DocumentDto | undefined, isNew: boolean): string {
  if (isNew || !doc) return type === 'CASH_IN' ? 'Новый приходный ордер' : 'Новый расходный ордер';
  const suffix = doc.deletedAt ? ' (удалён)' : (STATUS_SUFFIX[doc.status] ?? '');
  return `${prefixOf(type)}-${doc.number}/${doc.numberYear}${suffix}`;
}

/**
 * Ордер, созданный из документа («Создать приходный ордер» в продаже), сразу разнесён на него
 * (§7): строка-основание в сетке заполнена суммой ордера. Но не больше долга по документу, и пока
 * владелец меняет «Сумму», строка за ней следует — иначе частичная оплата (700 при долге 2200)
 * уходила бы с разнесением 2200 > 700 и отказом «Сумма разнесения превышает сумму платежа».
 * Строки, которые владелец правил сам, сюда не попадают (вызывающий код проверяет `rowsDirty`).
 */
export function refitPrefill(rows: AllocationRow[], prefillDocumentId: string, amount: string | null): AllocationRow[] {
  return rows.map((row) => {
    if (row.documentId !== prefillDocumentId) return row;
    const fitted = amount && dec(amount).gt(0) ? Decimal.min(dec(amount), dec(row.debt)) : dec(0);
    return { ...row, amount: fitted.gt(0) ? fitted.toFixed(2) : null };
  });
}

/**
 * Сумма ордера уменьшена, а разнесение не правилось вручную: оно подгоняется под новую сумму —
 * с конца, чтобы не рвать начало FIFO. Ручное разнесение на документ, из которого создан ордер
 * («Создать приходный ордер»), тоже ручное по устройству; без подгонки правка суммы 700 → 300
 * упиралась бы в «Сумма разнесения превышает сумму платежа» и требовала лезть в сетку.
 */
export function clampAllocations(allocations: { documentId: string; amount: string }[], amount: string | null): { documentId: string; amount: string }[] {
  let excess = allocations.reduce((sum, a) => sum.plus(dec(a.amount)), dec(0)).minus(amount && dec(amount).gt(0) ? dec(amount) : dec(0));
  if (excess.lte(0)) return allocations;
  const kept: { documentId: string; amount: string }[] = [];
  for (const a of [...allocations].reverse()) {
    const cut = Decimal.min(dec(a.amount), excess.gt(0) ? excess : dec(0));
    excess = excess.minus(cut);
    const left = dec(a.amount).minus(cut);
    if (left.gt(0)) kept.unshift({ documentId: a.documentId, amount: left.toFixed(2) });
  }
  return kept;
}
